// Composant d'image optimisée avec chargement progressif
import React from 'react';
import { useProgressiveImage, type UseProgressiveImageOptions } from '../Hooks/useProgressiveImage';

export interface OptimizedImageProps extends Omit<UseProgressiveImageOptions, 'fallbackSrc'> {
    fileId?: string;
    src?: string;
    alt: string;
    className?: string;
    style?: React.CSSProperties;
    fallbackSrc?: string;
    loadOriginal?: boolean;  // Charger l'original après la qualité initiale
    onLoad?: () => void;
    instanceId?: string; // ID de l'instance pour le cache
    componentId?: string; // ID du composant pour le cache
}

/**
 * Composant d'image avec chargement progressif automatique
 * - Détection automatique API vs Local
 * - Chargement en basse qualité puis haute qualité
 * - Fallback automatique si timeout
 */
export function OptimizedImage({
    fileId,
    src,
    alt,
    className = '',
    style = {},
    fallbackSrc,
    loadOriginal = true,
    onLoad,
    instanceId,
    componentId,
    ...options
}: OptimizedImageProps) {
    // Utiliser fileId ou src
    const source = fileId || src;

    const { src: loadedSrc, isLoading, currentQuality, error } = useProgressiveImage(
        source,
        {
            ...options,
            fallbackSrc,
            loadOriginal,
            instanceId,
            componentId
        }
    );

    // Effet onLoad quand l'image est chargée
    React.useEffect(() => {
        if (!isLoading && loadedSrc && onLoad) {
            onLoad();
        }
    }, [isLoading, loadedSrc, onLoad]);

    if (error && !loadedSrc) {
        return (
            <div
                className={`optimized-image-error ${className}`}
                style={{
                    ...style,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f0f0f0',
                    color: '#666'
                }}
            >
                <span>⚠️ Image non disponible</span>
            </div>
        );
    }

    return (
        <div
            className={`optimized-image-container ${className}`}
            style={{ 
                position: 'relative', 
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                ...style 
            }}
        >
            {loadedSrc && (
                <img
                    src={loadedSrc}
                    alt={alt}
                    className={`optimized-image ${isLoading ? 'loading' : 'loaded'}`}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        // Transition subtile uniquement pour le premier chargement
                        transition: isLoading ? 'opacity 0.3s ease-in-out' : 'none',
                        opacity: isLoading ? 0.7 : 1
                        // Pas de blur : l'original swap de manière invisible
                    }}
                    // Pas de loading="lazy" car nous gérons le lazy loading avec IntersectionObserver
                />
            )}

            {isLoading && (
                <div
                    className="optimized-image-loader"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0,0,0,0.05)'
                    }}
                >
                    <div className="spinner-small"></div>
                </div>
            )}

            {currentQuality !== null && (
                <div
                    className="optimized-image-quality-badge"
                    style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        backgroundColor: currentQuality >= 95 ? 'rgba(76,175,80,0.8)' : 'rgba(0,0,0,0.6)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        pointerEvents: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}
                >
                    {isLoading && <span style={{ fontSize: '8px' }}>⏳</span>}
                    {currentQuality}%
                    {currentQuality >= 95 && !isLoading && <span style={{ fontSize: '8px' }}>✓</span>}
                </div>
            )}
        </div>
    );
}

